import React, { useState } from 'react'
import { Button } from 'react-bootstrap'
import { Undo, Redo, ZoomIn, ZoomOut, Trash2, Eye, EyeOff, RotateCcw, Keyboard } from 'lucide-react'
import NavigationControls from './NavigationControls'
import ModelSelector from './ModelSelector'
import PillsDataset from '../../../PillsDataset'
import { buttonStyles } from '../styles'
import ShortcutsModal from '../../../modals/ShortcutsModal'

interface CanvasToolsProps {
  onBackToGallery: () => void
  onPrevious: () => void
  onNext: () => void
  onSubmit: () => void
  hasAnnotations: boolean
  showConfirmDialog: () => void
  activeTab: string
  onModelSelect: () => void
  onRunInference: () => void
  inferenceLoading: boolean
  onZoomIn: () => void
  onZoomOut: () => void
  onResetZoom: () => void
  onUndo: () => void
  onRedo: () => void
  onDelete: () => void
  showDelete: boolean
  showBBoxes: boolean
  onToggleBBoxes: () => void
  datasetName?: string
}

const CanvasTools: React.FC<CanvasToolsProps> = ({
  onBackToGallery,
  onPrevious,
  onNext,
  onSubmit,
  hasAnnotations,
  showConfirmDialog,
  activeTab,
  onModelSelect,
  onRunInference,
  inferenceLoading,
  onZoomIn,
  onZoomOut,
  onResetZoom,
  onUndo,
  onRedo,
  onDelete,
  showDelete,
  showBBoxes,
  onToggleBBoxes,
  datasetName,
}) => {
  const [showShortcuts, setShowShortcuts] = useState(false)
  return (
    <>
      <div className="d-flex align-items-center mb-3">
        <Button variant="primary" onClick={onBackToGallery} className="flex-grow-1">
          ← Back to Gallery
        </Button>
        <Button
          variant="outline-secondary"
          size="sm"
          className="ms-2"
          onClick={() => setShowShortcuts(true)}
          aria-label="Show keyboard shortcuts"
          title="Keyboard Shortcuts"
        >
          <Keyboard size={18} />
        </Button>
      </div>
      <div className="canvas-tools">
        <NavigationControls
          onPrevious={onPrevious}
          onNext={onNext}
          onSubmit={onSubmit}
          hasAnnotations={hasAnnotations}
          showConfirmDialog={showConfirmDialog}
          submitLabel={activeTab === 'predictions' ? 'Submit Predictions' : 'Submit Annotations'}
        />
        {/* Model Navigation */}
        {activeTab === 'content' && (
          <ModelSelector
            onModelSelect={onModelSelect}
            onRunInference={onRunInference}
            inferenceLoading={inferenceLoading}
          />
        )}
        {/* Canvas Navigation */}
        <div className="tool-group mb-3">
          <h6 className="text-white mt-1 small">Zoom Controls</h6>
          <div className="row g-2">
            <div className="col-6">
              <Button
                className="w-100"
                variant="outline-secondary"
                onClick={onZoomIn}
                style={buttonStyles.zoomControl}
                onMouseEnter={(e) =>
                  Object.assign(e.currentTarget.style, buttonStyles.zoomControlHover)
                }
                onMouseLeave={(e) => Object.assign(e.currentTarget.style, buttonStyles.zoomControl)}
              >
                <ZoomIn size={18} className="me-2" />
                In
              </Button>
            </div>
            <div className="col-6">
              <Button
                className="w-100"
                variant="outline-secondary"
                onClick={onZoomOut}
                style={buttonStyles.zoomControl}
                onMouseEnter={(e) =>
                  Object.assign(e.currentTarget.style, buttonStyles.zoomControlHover)
                }
                onMouseLeave={(e) => Object.assign(e.currentTarget.style, buttonStyles.zoomControl)}
              >
                <ZoomOut size={18} className="me-2" />
                Out
              </Button>
            </div>
            <div className="col-12">
              <Button
                className="w-100"
                variant="outline-secondary"
                onClick={onResetZoom}
                style={buttonStyles.zoomControl}
                onMouseEnter={(e) =>
                  Object.assign(e.currentTarget.style, buttonStyles.zoomControlHover)
                }
                onMouseLeave={(e) => Object.assign(e.currentTarget.style, buttonStyles.zoomControl)}
              >
                <RotateCcw size={18} className="me-2" />
                Reset Zoom
              </Button>
            </div>
          </div>
        </div>
        {/* Annotation Tools */}
        <div className="tool-group mb-3">
          <h6 className="text-white mt-1 small">Annotation</h6>
          <div className="row g-2">
            <div className="col-6">
              <Button
                className="w-100"
                variant="outline-secondary"
                onClick={onUndo}
                style={buttonStyles.annotationControl}
                onMouseEnter={(e) =>
                  Object.assign(e.currentTarget.style, buttonStyles.annotationControlHover)
                }
                onMouseLeave={(e) =>
                  Object.assign(e.currentTarget.style, buttonStyles.annotationControl)
                }
              >
                <Undo size={18} className="me-2" />
                Undo
              </Button>
            </div>
            <div className="col-6">
              <Button
                className="w-100"
                variant="outline-secondary"
                onClick={onRedo}
                style={buttonStyles.annotationControl}
                onMouseEnter={(e) =>
                  Object.assign(e.currentTarget.style, buttonStyles.annotationControlHover)
                }
                onMouseLeave={(e) =>
                  Object.assign(e.currentTarget.style, buttonStyles.annotationControl)
                }
              >
                <Redo size={18} className="me-2" />
                Redo
              </Button>
            </div>
            {showDelete && (
              <div className="col-12">
                <Button
                  className="w-100"
                  variant="outline-secondary"
                  onClick={onDelete}
                  style={buttonStyles.deleteControl}
                  onMouseEnter={(e) =>
                    Object.assign(e.currentTarget.style, buttonStyles.deleteControlHover)
                  }
                  onMouseLeave={(e) =>
                    Object.assign(e.currentTarget.style, buttonStyles.deleteControl)
                  }
                >
                  <Trash2 size={18} className="me-2" />
                  Delete
                </Button>
              </div>
            )}
          </div>
        </div>
        {/* View Options - Only show for bounding box projects */}
        {showDelete && (
          <div className="tool-group mt-2">
            <h6 className="text-white mb-1 small">View Options</h6>
            <div className="d-grid gap-2">
              <Button
                variant={showBBoxes ? 'primary' : 'outline-secondary'}
                onClick={onToggleBBoxes}
                style={buttonStyles.viewControl(showBBoxes)}
                onMouseEnter={(e) =>
                  Object.assign(e.currentTarget.style, buttonStyles.viewControlHover(showBBoxes))
                }
                onMouseLeave={(e) =>
                  Object.assign(e.currentTarget.style, buttonStyles.viewControl(showBBoxes))
                }
              >
                {showBBoxes ? (
                  <>
                    <EyeOff size={18} className="me-2" />
                    Hide Boxes
                  </>
                ) : (
                  <>
                    <Eye size={18} className="me-2" />
                    Show Boxes
                  </>
                )}
              </Button>
            </div>
          </div>
        )}
        {/* Dataset Info */}
        {datasetName && (
          <div className="tool-group mt-2">
            <h6 className="text-white mb-1 small">Dataset</h6>
            <PillsDataset datasetNames={[datasetName]} disabled={true} tiny={true} />
          </div>
        )}
      </div>
      <ShortcutsModal show={showShortcuts} onHide={() => setShowShortcuts(false)} />
    </>
  )
}

export default CanvasTools
